
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
// import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { STORAGE_KEYS } from '@constants';
import { setCredentials } from '@/store/slices/authSlice';
import * as roleService from '@/services/roleService';
import * as authService from '@/services/authService';
import * as companyService from '@/services/companyService';
import Api from '@/api/endpoints';
import './DashboardLayout.css';
import Button from '@/components/common/Button/Button';

import DashboardIcon from '@/assets/ adminsidebar/dashboard.svg';
import AnalyticsIcon from '@/assets/ adminsidebar/ analytics.svg';
import SearchIcon from '@/assets/Frame.svg';
import ChatIcon from '@/assets/ adminsidebar/ chat.svg';
import CatalogIcon from '@/assets/ adminsidebar/catalog.svg';
import TallyIcon from '@/assets/ adminsidebar/ tally.svg';
import ProfileIcon from '@/assets/ adminsidebar/Frame (11).svg';
import LogoutIcon from '@/assets/ adminsidebar/logout.svg';
import HeroImage from '@/assets/ adminsidebar/ hero.png';
import logo from '../../../assets/bizzLogo.webp';
import handshake from '../../../assets/ adminsidebar/Frame (9).svg';
// import GlobeIcon from '@/assets/Frame (7).png';
import LeadingIcon from '@/assets/Leading Icon.svg';
import { useTourManager } from '@/contexts/TourContext';
import MenuSlashIcon from '@/assets/MENU-slash.svg';
import RolesPermissionIcon from '@/assets/rolesPermissionIcon.svg';
import BusinessStructureIcon from '@/assets/unmappedsalesicon.svg';
import SalesTeamMappingIcon from '@/assets/salesTeamMapping.svg';
import financialDashboardIcon from '@/assets/tallyDashboardIcon/financildashboardicon.svg';
import receivablesIconNew from '@/assets/receivableIconnew.svg';
import payablesIconNew from '@/assets/tallyDashboardIcon/invoice.svg';
import sendRequestIcon from '@/assets/sidebarIcons/sendRequest.svg';
import copyIcon from '@/assets/copy.png';
import myRequestIcon from '@/assets/sidebarIcons/myRequestIcon.svg';
import skuSaleIcon from '@/assets/sidebarIcons/skuSaleIicon.svg';
import skusIcon from '@/assets/sidebarIcons/skusicon.svg';
import skuMappingIcon from '@/assets/sidebarIcons/skuMappingIcon.svg';
import secondarySalesIcon from '@/assets/sidebarIcons/secondrysalesicon.svg';
import transactionIcon from '@/assets/sidebarIcons/transactionIcon.svg';
import bankCashIcon from '@/assets/money.svg';
import arrowIcon from '@/assets/arrowIcon.svg';
import SimpleSearchBar from '@/components/common/SimpleSearchBar/SimpleSearchBar';
import NotificationDropdown from '@/components/common/NotificationDropdown/NotificationDropdown';
import SalesProfilePopover from '@/components/common/SalesProfilePopover/SalesProfilePopover';
import NavPermissionLink from '@/components/common/NavPermissionLink/NavPermissionLink';
import ChatbotWidget from '@/components/common/ChatbotWidget/ChatbotWidget';
import { toPagePx } from '@/utils/pageZoom';

export default function DashboardLayout({
  children,
  // companyName = 'Company',
  // companyLogo = null,
  // userName = 'User',
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const reduxDispatch = useDispatch();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('en');
  // const [activeMenuItem, setActiveMenuItem] = useState('');
  const [user, setUser] = useState(null);
  const [activeRoles, setActiveRoles] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [logoError, setLogoError] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [companyRefreshTrigger, setCompanyRefreshTrigger] = useState(0);
  const [showSalesProfilePopover, setShowSalesProfilePopover] = useState(false);
  const [activeDashboard, setActiveDashboard] = useState('business');
  const [copiedId, setCopiedId] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isSyncStale, setIsSyncStale] = useState(false);
  const [lastsyncDate, setLastSyncDate] = useState(null);

  const showPrimarySalesSetting = useSelector(
    state => state.tallyReports.showPrimarySales !== false
  );
  const selectedTallyCompanyId = useSelector(
    state => state.tallyReports.selectedCompanyId
  );

  const languageMenuRef = useRef(null);
  const languagePortalRef = useRef(null);

  // Function to close sidebar on mobile after navigation
  const handleNavLinkClick = () => {
    // Close sidebar on mobile (screen width < 992px)
    if (window.innerWidth < 992) {
      setSidebarOpen(false);
    }
  };

  const getUserFromStorage = () => {
    try {
      const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      return userData ? JSON.parse(userData) : null;
    } catch {
      return null;
    }
  };

  const storedUser = getUserFromStorage();

  // ── Centralized Onboarding Tour Manager ──────────────────────────────────────
  const { startTour } = useTourManager();
  const hasCompanyRoleAssignment = !!(
    user?.company_role_id ?? storedUser?.company_role_id
  );

  const isSalesmanUser = (u) =>
    u?.companyRole?.is_salesman === true ||
    u?.company_role?.is_salesman === true ||
    u?.company_role_is_salesman === true;

  /** Slim sidebar when the company role is marked salesman. */
  const isSalesOnlySidebar = hasCompanyRoleAssignment && (
    isSalesmanUser(user) || isSalesmanUser(storedUser)
  );

  // Get company data from localStorage
  const getCompanyFromStorage = () => {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.COMPANY_DATA);
      const parsed = data ? JSON.parse(data) : null;
      return parsed;
    } catch {
      return null;
    }
  };

  const companyData = getCompanyFromStorage();

  const isActive = path => (
    location.pathname === path || location.pathname.startsWith(path + '/')
  );
  const isAnyActive = (...paths) => paths.some(isActive);

  const isBusinessStructureActive = isAnyActive('/sales-mapping', '/industry-categories');
  const isUserManagementActive = isAnyActive('/user-role-management', '/roles-and-permissions', '/labels');
  const isProfileActive = isAnyActive('/my-profile', '/company-profile');
  const isTallyReportsV2Active = isAnyActive(
    '/tally-dashboard-v2',
    '/mfg-dashboard',
    '/mfg-sync-status',
    '/installations',
    '/not-onboarding-leads',
    '/transactions/sales-v2',
    '/sales/distributors-v2',
    '/dashboard/bank-and-cash',
    '/dashboard/receivables-v2',
    '/dashboard/payables-v2',
    '/invoices-v2',
    '/transactions',
    '/opsc-report',
    '/opsc'
  );
  const isSkuMappingV2Active = isAnyActive(
    '/secondary-sales/sku-sales-v2',
    '/secondary-sales/send-request-v2',
    '/secondary-sales/my-requests-v2',
    '/secondary-sales/requests-v2'
  );

  const isMessagesRoute = location.pathname.startsWith('/messages');

  // Check role from companyData - simple
  const isManufacturer = companyData?.role === 'manufacturer';
  const isDistributor = companyData?.role === 'distributor';

  /** Company employees need Business vs Financial switch in header (full nav). */
  // The business dashboard is commented out for both manu and dist
  const showBusinessFinancialToggle = hasCompanyRoleAssignment && !isSalesOnlySidebar;

  const getCompanyLogoUrl = () => {
    const companyData = getCompanyFromStorage();
    const logoPath = companyData?.company_logo;
    if (!logoPath) return null;

    if (logoPath.startsWith('http')) {
      return logoPath;
    }

    const baseUrl = (import.meta.env.VITE_API_BASE_URL || '').replace(
      '/api',
      ''
    );
    return `${baseUrl}${logoPath.startsWith('/') ? logoPath : `/${logoPath}`}`;
  };

  const actualCompanyLogo = getCompanyLogoUrl();
  const actualCompanyName = getCompanyFromStorage()?.company_name;

  const checkSyncStale = useCallback(async (companyId) => {
    if (!companyId || !isDistributor) return;

    const storageKey = `${STORAGE_KEYS.SYNC_STALE}_${companyId}`;

    try {
      const response = await Api.tallyReports.getLastSyncBatch(companyId);

      if (!response?.success || !response?.data?.lastSyncBatch?.length) {
        localStorage.setItem(storageKey, 'false');
        setIsSyncStale(false);
        return;
      }

      const { lastSyncBatch, batchSummary } = response.data;

      const allCompleted = lastSyncBatch.every(r => r.status === 'completed');
      if (!allCompleted) {
        localStorage.setItem(storageKey, 'false');
        setIsSyncStale(false);
        return;
      }

      const batchStartTime = batchSummary?.batchStartTime;
      if (!batchStartTime) {
        localStorage.setItem(storageKey, 'false');
        setIsSyncStale(false);
        return;
      }

      const twoDaysAgo = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000);
      const isStale = new Date(batchStartTime) < twoDaysAgo;

      localStorage.setItem(storageKey, String(isStale));
      setIsSyncStale(isStale);
      setLastSyncDate(batchStartTime);
    } catch {
      setIsSyncStale(false);
    }
  }, [isDistributor]);

  // Initialize user data only once on mount
  useEffect(() => {
    const initializeData = async () => {
      try {
        setRolesLoading(true);

        const roles = await roleService.getActiveRoles();
        setActiveRoles(roles || []);

        const storedUserData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
        let currentUser = null;

        if (storedUserData) {
          currentUser = JSON.parse(storedUserData);
        } else {
          try {
            const userData = await authService.getCurrentUser();
            currentUser = userData?.data || userData;
            if (currentUser) {
              localStorage.setItem(
                STORAGE_KEYS.USER_DATA,
                JSON.stringify(currentUser)
              );
            }
          } catch (error) {
            console.error('Failed to fetch user data:', error);
          }
        }

        setUser(currentUser);

        if (currentUser) {
          const token =
            localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) ||
            localStorage.getItem('auth_token');
          reduxDispatch(setCredentials({ user: currentUser, token }));
        }

        if (currentUser?.company_role_id) {
          const crName =
            currentUser?.companyRole?.role_name ||
            currentUser?.company_role?.role_name ||
            '';
          setUserRole(
            crName ? crName.toLowerCase().replace(/\s+/g, '_') : 'company_user'
          );
        } else if (currentUser?.role_id && roles) {
          const userRoleData = roles.find(
            role => role.id === currentUser.role_id
          );
          if (userRoleData) {
            const roleKey = (
              userRoleData.key ||
              userRoleData.name ||
              userRoleData.role_name ||
              ''
            ).toLowerCase();
            setUserRole(roleKey);
          }
        }
      } catch (error) {
        console.error('Failed to initialize user data:', error);
      } finally {
        setRolesLoading(false);
      }
    };

    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Fetch and save company data if not in localStorage
  useEffect(() => {
    const fetchAndSaveCompanyData = async () => {
      const storedUser = getUserFromStorage();
      if (storedUser?.company_id) {
        const existingCompany = getCompanyFromStorage();
        if (!existingCompany) {
          try {
            const companyResponse = await companyService.getCompanyById(
              storedUser.company_id
            );
            if (companyResponse?.success && companyResponse?.data) {
              localStorage.setItem(
                STORAGE_KEYS.COMPANY_DATA,
                JSON.stringify(companyResponse.data)
              );
              setCompanyRefreshTrigger(prev => prev + 1);
            }
          } catch (error) {
            console.error('Failed to fetch company data:', error);
          }
        }
      }
    };

    fetchAndSaveCompanyData();
  }, []);

  // Handle menu expansion based on route changes
  useEffect(() => {
    const tallyRoutes = [
      '/tallyReports',
      '/manufacturers',
      '/distributors',
      '/outlets',
      '/invoices',
      '/skus',
      '/skus-v2',
    ];
    const isTallyRoute = tallyRoutes.some(route =>
      location.pathname.startsWith(route)
    );
    if (isTallyRoute && !expandedMenus.tallyReports) {
      setExpandedMenus(prev => ({ ...prev, tallyReports: true }));
    }

    const transactionRoutes = [
      '/transactions/sales',
      '/transactions/purchase',
      '/transactions/receipt',
      '/transactions/payment',
    ];
    const isTransactionRoute = transactionRoutes.some(route =>
      location.pathname.startsWith(route)
    );
    if (isTransactionRoute && !expandedMenus.transactions) {
      setExpandedMenus(prev => ({ ...prev, transactions: true }));
    }

    const transactionV2Routes = [
      '/transactions/sales-v2',
      '/transactions/purchase-v2',
      '/transactions/receipt-v2',
      '/transactions/payment-v2',
    ];
    const isTransactionV2Route = transactionV2Routes.some(route =>
      location.pathname.startsWith(route)
    );
    if (isTransactionV2Route && !expandedMenus.transactionsV2) {
      setExpandedMenus(prev => ({
        ...prev,
        tallyReportsV2: true,
        transactionsV2: true,
      }));
    }

    const invoiceV2Routes = [
      '/invoices-v2',
      '/tally-dashboard-v2',
      '/dashboard/receivables-v2',
      '/dashboard/payables-v2',
      '/dashboard/party-sku-deep-dive',
      '/opsc-report',
      '/opsc',
    ];
    const isInvoiceV2Route = invoiceV2Routes.some(route =>
      location.pathname.startsWith(route)
    );
    if (isInvoiceV2Route && !expandedMenus.tallyReportsV2) {
      setExpandedMenus(prev => ({
        ...prev,
        tallyReportsV2: true,
        tallyDashboard: true,
      }));
    }

    const skuMappingRoutes = [
      '/secondary-sales/sku-sales',
      '/secondary-sales/sku-sales/mapped-distributors',
      '/secondary-sales/send-request',
      '/secondary-sales/my-requests',
      '/secondary-sales/requests',
      '/secondary-sales/mapping-logs',
    ];
    const isSkuMappingRoute = skuMappingRoutes.some(route =>
      location.pathname.startsWith(route)
    );
    if (isSkuMappingRoute && !expandedMenus.skuMapping) {
      setExpandedMenus(prev => ({ ...prev, skuMapping: true }));
    }

    const skuMappingV2Routes = [
      '/secondary-sales/sku-sales-v2',
      '/secondary-sales/sku-sales-v2/mapping-details',
      '/secondary-sales/sku-sales/mapped-distributors',
      '/secondary-sales/send-request-v2',
      '/secondary-sales/my-requests-v2',
      '/secondary-sales/requests',
      '/secondary-sales/requests-v2',
      '/secondary-sales/mapping-logs-v2',
      '/skus-v2',
    ];
    const isSkuMappingV2Route = skuMappingV2Routes.some(route =>
      location.pathname.startsWith(route)
    );
    if (isSkuMappingV2Route && !expandedMenus.skuMappingV2) {
      setExpandedMenus(prev => ({ ...prev, skuMappingV2: true }));
    }

    const businessStructureRoutes = ['/sales-mapping', '/distributor-staff-mapping'];
    const isBusinessStructureRoute = businessStructureRoutes.some(route =>
      location.pathname.startsWith(route)
    );
    if (isBusinessStructureRoute && !expandedMenus.businessStructure) {
      setExpandedMenus(prev => ({ ...prev, businessStructure: true }));
    }

    const userManagementRoutes = [
      '/user-role-management',
      '/roles-and-permissions',
    ];
    const isUserManagementRoute = userManagementRoutes.some(route =>
      location.pathname.startsWith(route)
    );
    if (isUserManagementRoute && !expandedMenus.userManagement) {
      setExpandedMenus(prev => ({ ...prev, userManagement: true }));
    }

    const masterRoutes = [
      '/labels',
      '/user-role-management',
      '/industry-categories',
    ];
    const isMasterRoute = masterRoutes.some(route =>
      location.pathname.startsWith(route)
    );
    if (isMasterRoute && !expandedMenus.master) {
      setExpandedMenus(prev => ({ ...prev, master: true }));
    }
  }, [location.pathname]);

  // Sync activeDashboard state with current route
  useEffect(() => {
    const financialRoutes = [
      '/financial-dashboard',
      '/tally-dashboard',
      '/tally-dashboard-v2',
      '/mfg-dashboard',
      '/tally',
      '/transactions',
      '/invoices-v2',
      '/secondary-sales',
      '/receivable',
      '/receivables',
      '/dashboard/receivables-v2',
      '/payable',
      '/dashboard/payables-v2',
      '/sales/distributors',
      '/sales/distributors-v2',
      '/sales/distributors2',
      '/sales/outlets',
      '/sales/invoices',
      '/sales/invoices2',
      '/sales/purchase-invoices2',
      '/sales/outlets2',
      '/tally/master/skus',
      '/skus-v2',
      '/tally-sync',
      '/mfg-sync-status',
      '/dashboard/bank-and-cash',
      '/dashboard/party-sku-deep-dive',
      '/opsc-report',
      '/opsc',
      '/dump-report',
    ];

    // Common routes that should inherit dashboard from parent context
    const commonRoutes = [
      '/sales-mapping',
      '/distributor-staff-mapping',
      '/industry-categories',
      '/user-role-management',
      '/roles-and-permissions',
      '/labels',
    ];

    const isCommonRoute = commonRoutes.some(
      route =>
        location.pathname === route || location.pathname.startsWith(route + '/')
    );

    if (isCommonRoute) {
      if (isManufacturer || isDistributor) {
        setActiveDashboard('financial');
      }
    } else {
      const isFinancialRoute = financialRoutes.some(
        route =>
          location.pathname === route ||
          location.pathname.startsWith(route + '/')
      );
      if (isManufacturer || isDistributor) {
        setActiveDashboard('financial');
      } else {
        setActiveDashboard(isFinancialRoute ? 'financial' : 'business');
      }
    }
  }, [location.pathname, isManufacturer, isDistributor]);

  const toggleMenu = menuKey => {
    setExpandedMenus(prev => {
      const isCurrentlyOpen = prev[menuKey];
      const allClosed = Object.keys(prev).reduce((acc, k) => ({ ...acc, [k]: false }), {});
      return { ...allClosed, [menuKey]: !isCurrentlyOpen };
    });
  };

  const toggleSubMenu = menuKey => {
    setExpandedMenus(prev => ({ ...prev, [menuKey]: !prev[menuKey] }));
  };

  const handleLogout = () => {

    const tourFlagsToPreserve = {};
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith('financial_dashboard_tour_started')) {
          tourFlagsToPreserve[k] = localStorage.getItem(k);
        }
      }
    } catch (error) {
      console.error('Failed to read tour flags from localStorage:', error);
    }

    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    localStorage.removeItem(STORAGE_KEYS.COMPANY_DATA);
    localStorage.removeItem('tally_selected_company_id');
    localStorage.clear();
    sessionStorage.clear();

    try {
      Object.entries(tourFlagsToPreserve).forEach(([k, v]) => {
        localStorage.setItem(k, v);
      });
    } catch (error) {
      console.error('Failed to restore tour flags in localStorage:', error);

    }

    window.location.replace('/');
  };

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        languageMenuOpen &&
        languageMenuRef.current &&
        !languageMenuRef.current.contains(event.target) &&
        languagePortalRef.current &&
        !languagePortalRef.current.contains(event.target)
      ) {
        setLanguageMenuOpen(false);
      }
    };

    if (languageMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [languageMenuOpen]);

  useEffect(() => {
    const handleEscape = e => {
      if (e.key === 'Escape' && mobileSearchOpen) {
        setMobileSearchOpen(false);
      }
    };

    const handleClickOutside = e => {
      if (mobileSearchOpen) {
        const searchDropdown = document.querySelector(
          '.admin-mobile-search-dropdown'
        );
        const searchButton = document.querySelector('.admin-mobile-search-btn');

        if (
          searchDropdown &&
          !searchDropdown.contains(e.target) &&
          !searchButton.contains(e.target)
        ) {
          setMobileSearchOpen(false);
        }
      }
    };

    if (mobileSearchOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileSearchOpen]);

  // const handleLanguageSelect = (lang, e) => {
  //   if (e) {
  //     e.preventDefault();
  //     e.stopPropagation();
  //   }
  //   setCurrentLanguage(lang);
  //   setLanguageMenuOpen(false);
  // };

  // const handleSearch = searchTerm => {
  //   if (searchTerm && searchTerm.trim()) {
  //     navigate('/explore', { state: { searchTerm: searchTerm.trim() } });
  //   } else {
  //     navigate('/explore');
  //   }
  // };

  const refreshCompanyData = () => {
    setCompanyRefreshTrigger(prev => prev + 1);
  };

  useEffect(() => {
    const handleCompanyUpdate = event => {
      if (event.detail?.companyId === user?.company_id) {
        refreshCompanyData();
      }
    };
    const handleSyncCompleted = () => setIsSyncStale(false);
    const handleSyncStaleUpdate = e => {
      if (typeof e.detail?.isStale === 'boolean') {
        setIsSyncStale(e.detail.isStale);
      }
    };

    window.addEventListener('companyDataUpdated', handleCompanyUpdate);
    window.addEventListener('syncCompleted', handleSyncCompleted);
    window.addEventListener('syncStaleUpdate', handleSyncStaleUpdate);
    return () => {
      window.removeEventListener('companyDataUpdated', handleCompanyUpdate);
      window.removeEventListener('syncCompleted', handleSyncCompleted);
      window.removeEventListener('syncStaleUpdate', handleSyncStaleUpdate);
    };
  }, [user?.company_id]);

  useEffect(() => {
    if (!isDistributor) return;
    const companyId = selectedTallyCompanyId || (() => {
      try {
        const n = Number(localStorage.getItem('tally_selected_company_id'));
        return Number.isFinite(n) && n > 0 ? n : null;
      } catch { return null; }
    })();
    if (!companyId) return;
    checkSyncStale(companyId);
  }, [selectedTallyCompanyId, isDistributor, checkSyncStale]);

  const navItems = useMemo(
    () => [
      {
        key: 'dashboard',
        to: '/dashboard',
        icon: DashboardIcon,
        label: 'Dashboard',
      },
      {
        key: 'analytics',
        to: '',
        icon: AnalyticsIcon,
        label: 'Analytics',
      },
      {
        key: 'explore',
        to: '/explore',
        icon: AnalyticsIcon,
        label: 'Explore Partners',
      },
      {
        key: 'chat',
        to: '',
        icon: ChatIcon,
        label: 'Chat',
      },
      ...(isManufacturer
        ? [
          {
            key: 'products',
            to: '/products',
            icon: CatalogIcon,
            label: 'Catalogue',
          },
        ]
        : []),
      ...(isDistributor
        ? [
          {
            key: 'requirements',
            to: '/requirements',
            icon: CatalogIcon,
            label: 'Requirements',
          },
        ]
        : []),
      {
        key: 'my-deals',
        to: '',
        icon: handshake,
        label: 'My Deals',
      },
      {
        type: 'group',
        key: 'tallyReports',
        icon: TallyIcon,
        label: ' Analytics',
        children: [
          {
            key: 'dashboards',
            to: '',
            icon: 'bi bi-bar-chart',
            label: 'Dashboards',
          },
          {
            to: '',
            type: 'group',
            key: 'transactions',
            icon: 'bi bi-receipt',
            label: 'Transactions',
            children: [
              ...(isManufacturer
                ? [
                  {
                    key: 'sales',
                    to: '',
                    icon: 'bi bi-cart-check',
                    label: 'Sales to distributors',
                  },
                  {
                    key: 'purchase',
                    to: '',
                    icon: 'bi bi-cart-plus',
                    label: 'Purchase',
                  },
                  {
                    key: 'receipt',
                    to: '',
                    icon: 'bi bi-arrow-down-circle',
                    label: 'Receipt',
                  },
                  {
                    key: 'payment',
                    to: '',
                    icon: 'bi bi-arrow-up-circle',
                    label: 'Payment',
                  },
                ]
                : []),
              ...(isDistributor
                ? [
                  {
                    key: 'sales',
                    to: '',
                    icon: 'bi bi-cart-check',
                    label: 'Sales to outlets',
                  },
                  {
                    key: 'purchase',
                    to: '',
                    icon: 'bi bi-cart-plus',
                    label: 'Purchase from supplier',
                  },
                  {
                    key: 'receipt',
                    to: '',
                    icon: 'bi bi-arrow-down-circle',
                    label: 'Receipt',
                  },
                  {
                    key: 'payment',
                    to: '',
                    icon: 'bi bi-arrow-up-circle',
                    label: 'Payment',
                  },
                ]
                : []),
            ],
          },
          ...(!isDistributor
            ? [
              {
                key: 'secondary-sales',
                to: '',
                icon: 'bi bi-shop',
                label: 'Sales',
              },
            ]
            : []),
          ...(!isManufacturer
            ? [
              {
                key: 'supplier',
                to: '',
                icon: 'bi bi-building',
                label: 'Supplier',
              },
            ]
            : []),
          {
            key: 'skus',
            to: '',
            icon: 'bi bi-tags',
            label: 'SKUs',
          },
          {
            key: 'settings',
            to: '/tally-sync',
            icon: 'bi bi-gear',
            label: 'Settings',
          },
          ...(isManufacturer
            ? [
              {
                key: 'sync-status',
                to: '/mfg-sync-status',
                icon: 'bi bi-activity',
                label: 'Sync Status',
              },
              {
                key: 'installations',
                to: '/installations',
                icon: 'bi bi-tools',
                label: 'Installations',
              },
              {
                key: 'not-onboarding-leads',
                to: '/not-onboarding-leads',
                icon: 'bi bi-person-x',
                label: 'Not Onboarding',
              },
            ]
            : []),
        ],
      },
      {
        type: 'group',
        key: 'profile',
        icon: ProfileIcon,
        label: 'Profile',
        children: [
          {
            key: 'MyProfile',
            to: '/my-profile',
            icon: 'bi bi-person-badge',
            label: 'My Profile',
          },
          {
            key: 'saved-profiles',
            to: '/saved-profiles',
            icon: 'bi bi-bookmark',
            label: 'View Saved Profiles',
          },
        ],
      },
    ],
    [isManufacturer, isDistributor, userRole]
  );

  return (
    <div
      className="admin-layout-container"
    >
      <aside
        className={`modern-admin-sidebar admin-sidebar-flex ${sidebarOpen ? 'open' : ''} ${isSidebarCollapsed ? 'collapsed' : ''}`}
      >
        <div className="sidebar-brand">
          <div className="sidebar-close-btn-wrapper">
            <button
              type="button"
              className="sidebar-close-btn"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close sidebar"
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
          <div className="brand-logo">
            <NavPermissionLink unrestricted to="/">
              <img src={logo} alt="Bizz+" />
            </NavPermissionLink>
          </div>
          <button
            type="button"
            className="sidebar-collapse-toggle-btn"
            onClick={() => setIsSidebarCollapsed(prev => !prev)}

            aria-label={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <img
              src={arrowIcon}
              alt="Toggle"
              className={`collapse-arrow-icon ${isSidebarCollapsed ? 'collapsed' : 'expanded'}`}
            />
          </button>
        </div>

        <nav className={`sidebar-nav${isSalesOnlySidebar ? ' sidebar-nav--sales' : ''}`}>
          {isSalesOnlySidebar ? (
            <>
              <div className="nav-group">
                <div
                  className={`nav-link-modern nav-group-toggle ${isSidebarCollapsed ? (isTallyReportsV2Active ? 'active' : '') : (expandedMenus.tallyReportsV2 || isTallyReportsV2Active ? 'active' : '')}`}
                  onClick={() => isSidebarCollapsed ? (navigate(isManufacturer ? '/mfg-dashboard' : '/tally-dashboard-v2'), handleNavLinkClick()) : toggleMenu('tallyReportsV2')}
                  data-tooltip="Analytics"
                >
                  <div className="nav-icon">
                    <img src={TallyIcon} alt="" className="nav-icon-standard" />
                  </div>
                  <span className="nav-text">Analytics</span>
                  <i className={`bi bi-chevron-${expandedMenus.tallyReportsV2 ? 'down' : 'right'} nav-chevron`}></i>
                </div>
                {expandedMenus.tallyReportsV2 && (
                  <div className="nav-submenu">
                    <div className="nav-group">
                      <div
                        className={`nav-link-modern nav-group-toggle nav-submenu-item ${isActive('/tally-dashboard-v2') || isActive('/mfg-dashboard') || isActive('/transactions/sales-v2') || isActive('/sales/distributors-v2') ? 'has-active-child' : ''}`}
                      >
                        <div
                          className="nav-link-content"
                          onClick={e => {
                            e.stopPropagation();
                            navigate(isManufacturer ? '/mfg-dashboard' : '/tally-dashboard-v2');
                            handleNavLinkClick();
                          }}
                          style={{ display: 'flex', alignItems: 'center', flex: 1, cursor: 'pointer' }}
                        >
                          <div className="nav-icon">
                            <i className="bi bi-bar-chart"></i>
                          </div>
                          <span className="nav-text">Dashboard</span>
                        </div>
                        <i
                          className={`bi bi-chevron-${expandedMenus.tallyDashboard ? 'down' : 'right'} nav-chevron`}
                          onClick={e => {
                            e.stopPropagation();
                            toggleSubMenu('tallyDashboard');
                          }}
                          style={{ cursor: 'pointer' }}
                        ></i>
                      </div>
                      {expandedMenus.tallyDashboard && (
                        <div className="nav-submenu">
                          <NavPermissionLink
                            unrestricted
                            to={isDistributor ? '/transactions/sales-v2' : '/sales/distributors-v2'}
                            className={`nav-link-modern nav-submenu-item ${isActive('/transactions/sales-v2') || isActive('/sales/distributors-v2') || isActive('/sales/outlets2') || isActive('/sales/invoices2') || isActive('/sales/purchase-invoices2') || isActive('/invoices-v2') ? 'active' : ''}`}
                            onClick={handleNavLinkClick}
                          >
                            <div className="nav-icon">
                              <img src={secondarySalesIcon} alt="" className="nav-icon-standard" />
                            </div>
                            <span className="nav-text">{isDistributor ? 'Sales to outlets' : 'Sales'}</span>
                          </NavPermissionLink>
                          {isManufacturer && (
                            <NavPermissionLink
                              unrestricted
                              to="/opsc-report"
                              className={`nav-link-modern nav-submenu-item ${isActive('/opsc-report') || isActive('/opsc') ? 'active' : ''}`}
                              onClick={handleNavLinkClick}
                            >
                              <div className="nav-icon">
                                <i className="bi bi-file-earmark-spreadsheet" style={{ fontSize: '18px' }}></i>
                              </div>
                              <span className="nav-text">OPSC Report</span>
                            </NavPermissionLink>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {isManufacturer && (
                <>
                  <NavPermissionLink
                    unrestricted
                    to="/mfg-sync-status"
                    data-tooltip="Sync Status"
                    className={`nav-link-modern ${isActive('/mfg-sync-status') ? 'active' : ''}`}
                    onClick={handleNavLinkClick}
                  >
                    <div className="nav-icon">
                      <i className="bi bi-activity"></i>
                    </div>
                    <span className="nav-text">Sync Status</span>
                  </NavPermissionLink>
                  <NavPermissionLink
                    unrestricted
                    to="/installations"
                    data-tooltip="Installations"
                    className={`nav-link-modern ${isActive('/installations') ? 'active' : ''}`}
                    onClick={handleNavLinkClick}
                  >
                    <div className="nav-icon">
                      <i className="bi bi-tools"></i>
                    </div>
                    <span className="nav-text">Installations</span>
                  </NavPermissionLink>
                  <NavPermissionLink
                    unrestricted
                    to="/not-onboarding-leads"
                    data-tooltip="Not Onboarding"
                    className={`nav-link-modern ${isActive('/not-onboarding-leads') ? 'active' : ''}`}
                    onClick={handleNavLinkClick}
                  >
                    <div className="nav-icon">
                      <i className="bi bi-person-x"></i>
                    </div>
                    <span className="nav-text">Not Onboarding</span>
                  </NavPermissionLink>
                  <NavPermissionLink
                    unrestricted
                    to="/dump-report"
                    data-tooltip="Dump Report"
                    className={`nav-link-modern ${isActive('/dump-report') ? 'active' : ''}`}
                    onClick={handleNavLinkClick}
                  >
                    <div className="nav-icon">
                      <img src={transactionIcon} alt="" className="nav-icon-standard" />
                    </div>
                    <span className="nav-text">Dump Report</span>
                  </NavPermissionLink>
                </>
              )}

              <div className="nav-group">
                <div
                  className={`nav-link-modern nav-group-toggle ${isSidebarCollapsed ? (isProfileActive ? 'active' : '') : (expandedMenus.profile || isProfileActive ? 'active' : '')}`}
                  onClick={() => isSidebarCollapsed ? (navigate('/my-profile'), handleNavLinkClick()) : toggleMenu('profile')}
                  data-tooltip="Profile"
                >
                  <div className="nav-icon">
                    <img src={ProfileIcon} alt="" className="nav-icon-standard" />
                  </div>
                  <span className="nav-text">Profile</span>
                  <i className={`bi bi-chevron-${expandedMenus.profile ? 'down' : 'right'} nav-chevron`}></i>
                </div>
                {expandedMenus.profile && (
                  <div className="nav-submenu">
                    <NavPermissionLink
                      unrestricted
                      to="/my-profile"
                      className={`nav-link-modern nav-submenu-item ${isActive('/my-profile') ? 'active' : ''}`}
                      onClick={handleNavLinkClick}
                    >
                      <div className="nav-icon">
                        <i className="bi bi-person-badge"></i>
                      </div>
                      <span className="nav-text">My Profile</span>
                    </NavPermissionLink>
                  </div>
                )}
              </div>

              <div style={{ flex: 1 }} />

              <div className="nav-group spp-nav-group">
                <div
                  className="nav-link-modern nav-group-toggle spp-profile-trigger"
                  onClick={() => setShowSalesProfilePopover(v => !v)}
                >
                  <div className="spp-trigger-avatar">
                    {user?.user_name
                      ? (() => {
                        const parts = user.user_name.trim().split(' ');
                        return parts.length >= 2
                          ? (parts[0][0] + parts[1][0]).toUpperCase()
                          : user.user_name.slice(0, 2).toUpperCase();
                      })()
                      : 'U'}
                  </div>
                  <div className="spp-trigger-info">
                    <span className="spp-trigger-name">
                      {user?.user_name || 'User'}
                    </span>
                    <span className="spp-trigger-role">
                      {user?.companyRole?.role_name ||
                        user?.company_role?.role_name ||
                        user?.role_name ||
                        ''}
                    </span>
                  </div>
                </div>
                {showSalesProfilePopover && (
                  <div className="spp-popover-wrapper">
                    <SalesProfilePopover
                      onClose={() => setShowSalesProfilePopover(false)}
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Business Dashboard Items */}
              {activeDashboard === 'business' && (
                <>
                  {/* 
                  <NavPermissionLink
                    unrestricted
                    to="/dashboard"
                    className={`nav-link-modern ${isActive('/dashboard') ? 'active' : ''}`}
                    onClick={handleNavLinkClick}
                  >
                    <div className="nav-icon">
                      <img
                        src={DashboardIcon}
                        alt=""
                        className="nav-icon-standard"
                      />
                    </div>
                    <span className="nav-text">Dashboard</span>
                  </NavPermissionLink>
                  */}

                  {/* 
                  <NavPermissionLink
                    to="/explore"
                    className={`nav-link-modern ${isActive('/explore') ? 'active' : ''}`}
                    onClick={handleNavLinkClick}
                  >
                    <div className="nav-icon">
                      <img
                        src={AnalyticsIcon}
                        alt=""
                        className="nav-icon-standard"
                      />
                    </div>
                    <span className="nav-text">Explore Partners</span>
                  </NavPermissionLink>
                  */}

                  <NavPermissionLink
                    unrestricted
                    to="/messages"
                    className={`nav-link-modern ${isActive('/messages') ? 'active' : ''}`}
                    onClick={handleNavLinkClick}
                  >
                    <div className="nav-icon">
                      <img
                        src={ChatIcon}
                        alt=""
                        className="nav-icon-standard"
                      />
                    </div>
                    <span className="nav-text">Chat</span>
                  </NavPermissionLink>

                  {isManufacturer && (
                    <NavPermissionLink
                      to="/products"
                      className={`nav-link-modern ${isActive('/products') ? 'active' : ''}`}
                      onClick={handleNavLinkClick}
                      data-tour="catalog-sidebar-link"
                    >
                      <div className="nav-icon">
                        <img
                          src={CatalogIcon}
                          alt=""
                          className="nav-icon-standard"
                        />
                      </div>
                      <span className="nav-text">Catalogue</span>
                    </NavPermissionLink>
                  )}

                  {isDistributor && (
                    <NavPermissionLink
                      to="/requirements"
                      className={`nav-link-modern ${isActive('/requirements') ? 'active' : ''}`}
                      onClick={handleNavLinkClick}
                    >
                      <div className="nav-icon">
                        <img
                          src={CatalogIcon}
                          alt=""
                          className="nav-icon-standard"
                        />
                      </div>
                      <span className="nav-text">Requirements</span>
                    </NavPermissionLink>
                  )}

                  {/* Business Structure Menu */}
                  {(isManufacturer || isDistributor) && (
                    <div className="nav-group">
                      <div
                        className={`nav-link-modern nav-group-toggle ${expandedMenus.businessStructure ? 'active' : ''}`}
                        onClick={() => toggleMenu('businessStructure')}
                      >
                        <div className="nav-icon">
                          <img
                            src={BusinessStructureIcon}
                            alt=""
                            className="nav-icon-standard"
                          />
                        </div>
                        <span className="nav-text">Business Structure</span>
                        <i
                          className={`bi bi-chevron-${expandedMenus.businessStructure ? 'down' : 'right'} nav-chevron`}
                        ></i>
                      </div>
                      {expandedMenus.businessStructure && (
                        <div className="nav-submenu">
                          <NavPermissionLink
                            to={isManufacturer ? "/distributor-staff-mapping" : "/sales-mapping"}
                            data-tour="sales-mapping-sidebar-link"
                            className={`nav-link-modern nav-submenu-item ${isActive(isManufacturer ? '/distributor-staff-mapping' : '/sales-mapping') ? 'active' : ''}`}
                            onClick={handleNavLinkClick}
                          >
                            <div className="nav-icon">
                              <img
                                src={SalesTeamMappingIcon}
                                alt=""
                                className="nav-icon-standard"
                              />
                            </div>
                            <span className="nav-text">
                              {isDistributor ? 'Outlet Staff Mapping' : 'Distributor Staff Mapping'}
                            </span>
                          </NavPermissionLink>

                          <NavPermissionLink
                            to="/industry-categories"
                            className={`nav-link-modern nav-submenu-item ${isActive('/industry-categories') ? 'active' : ''}`}
                            onClick={handleNavLinkClick}
                          >
                            <div className="nav-icon">
                              <i className="bi bi-grid-3x3-gap"></i>
                            </div>
                            <span className="nav-text">
                              Category/Subcategory
                            </span>
                          </NavPermissionLink>
                        </div>
                      )}
                    </div>
                  )}

                  {/* User Management Menu */}
                  <div className="nav-group">
                    <div
                      className={`nav-link-modern nav-group-toggle ${expandedMenus.userManagement ? 'active' : ''}`}
                      onClick={() => toggleMenu('userManagement')}
                      data-tour="usermgmt-nav-toggle"
                    >
                      <div className="nav-icon">
                        <i className="bi bi-people"></i>
                      </div>
                      <span className="nav-text">User Management</span>
                      <i
                        className={`bi bi-chevron-${expandedMenus.userManagement ? 'down' : 'right'} nav-chevron`}
                      ></i>
                    </div>
                    {expandedMenus.userManagement && (
                      <div className="nav-submenu">
                        <NavPermissionLink
                          to="/user-role-management"
                          className={`nav-link-modern nav-submenu-item ${location.pathname === '/user-role-management' ? 'active' : ''}`}
                          onClick={handleNavLinkClick}
                          data-tour="usermgmt-nav-listings"
                        >
                          <div className="nav-icon">
                            <i className="bi bi-people"></i>
                          </div>
                          <span className="nav-text">Users Listing</span>
                        </NavPermissionLink>

                        <NavPermissionLink
                          to="/user-role-management/hierarchy"
                          className={`nav-link-modern nav-submenu-item ${isActive('/user-role-management/hierarchy') ? 'active' : ''}`}
                          onClick={handleNavLinkClick}
                        >
                          <div className="nav-icon">
                            <i className="bi bi-diagram-3"></i>
                          </div>
                          <span className="nav-text">Employee Hierarchy</span>
                        </NavPermissionLink>

                        <NavPermissionLink
                          to="/roles-and-permissions"
                          className={`nav-link-modern nav-submenu-item ${isActive('/roles-and-permissions') ? 'active' : ''}`}
                          onClick={handleNavLinkClick}
                        >
                          <div className="nav-icon">
                            <img
                              src={RolesPermissionIcon}
                              alt=""
                              className="nav-icon-standard"
                            />
                          </div>
                          <span className="nav-text">Roles & Permissions</span>
                        </NavPermissionLink>

                        <NavPermissionLink
                          to="/labels"
                          className={`nav-link-modern nav-submenu-item ${isActive('/labels') ? 'active' : ''}`}
                          onClick={handleNavLinkClick}
                        >
                          <div className="nav-icon">
                            <i className="bi bi-people-fill"></i>
                          </div>
                          <span className="nav-text">Teams</span>
                        </NavPermissionLink>

                        {/* <NavPermissionLink
                          to="/team-hierarchy"
                          className={`nav-link-modern nav-submenu-item ${isActive('/team-hierarchy') ? 'active' : ''}`}
                          onClick={handleNavLinkClick}
                        >
                          <div className="nav-icon">
                            <i className="bi bi-diagram-3"></i>
                          </div>
                          <span className="nav-text">Team Hierarchy</span>
                        </NavPermissionLink> */}
                      </div>
                    )}
                  </div>

                  {/* Profile Menu */}
                  <div className="nav-group">
                    <div
                      className={`nav-link-modern nav-group-toggle ${expandedMenus.profile ? 'active' : ''}`}
                      onClick={() => toggleMenu('profile')}
                    >
                      <div className="nav-icon">
                        <img
                          src={ProfileIcon}
                          alt=""
                          className="nav-icon-standard"
                        />
                      </div>
                      <span className="nav-text">Profile</span>
                      <i
                        className={`bi bi-chevron-${expandedMenus.profile ? 'down' : 'right'} nav-chevron`}
                      ></i>
                    </div>
                    {expandedMenus.profile && (
                      <div className="nav-submenu">
                        <NavPermissionLink
                          unrestricted
                          to="/my-profile"
                          className={`nav-link-modern nav-submenu-item ${isActive('/my-profile') ? 'active' : ''}`}
                          onClick={handleNavLinkClick}
                        >
                          <div className="nav-icon">
                            <i className="bi bi-person-badge"></i>
                          </div>
                          <span className="nav-text">My Profile</span>
                        </NavPermissionLink>

                        {/* COMMENTED OUT: Hide View Saved Profiles
                        <NavPermissionLink
                          unrestricted
                          to="/saved-profiles"
                          className={`nav-link-modern nav-submenu-item ${isActive('/saved-profiles') ? 'active' : ''}`}
                        >
                          <div className="nav-icon">
                            <i className="bi bi-bookmark"></i>
                          </div>
                          <span className="nav-text">View Saved Profiles</span>
                        </NavPermissionLink>
                        */}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Financial Dashboard Items */}
              {activeDashboard === 'financial' && (
                <>
                  {/* OLD Tally Reports - Hidden from sidebar but route still accessible via direct URL */}
                  {/* <div className="nav-group">
                    <div
                      className={`nav-link-modern nav-group-toggle ${expandedMenus.tallyReports ? 'active' : ''}`}
                      onClick={() => toggleMenu('tallyReports')}
                    >
                      <div className="nav-icon">
                        <img src={TallyIcon} alt="" className="nav-icon-standard" />
                      </div>
                      <span className="nav-text">Tally Reports</span>
                      <i
                        className={`bi bi-chevron-${expandedMenus.tallyReports ? 'down' : 'right'} nav-chevron`}
                      ></i>
                    </div>
                    {expandedMenus.tallyReports && (
                      <div className="nav-submenu">
                        <NavPermissionLink
                          to="/tally-dashboard"
                          className={`nav-link-modern nav-submenu-item ${isActive('/tally-dashboard') ? 'active' : ''}`}
                        >
                          <div className="nav-icon">
                            <i className="bi bi-bar-chart"></i>
                          </div>
                          <span className="nav-text">Dashboards</span>
                          
                        </NavPermissionLink>


                        <div className="nav-group">
                          <div
                            className={`nav-link-modern nav-group-toggle nav-submenu-item ${expandedMenus.transactions ? 'active' : ''}`}
                            onClick={e => {
                              e.stopPropagation();
                              toggleSubMenu('transactions');
                            }}
                          >
                            <div className="nav-icon">
                              <i className="bi bi-receipt"></i>
                            </div>
                            <span className="nav-text">Transactions</span>
                            <i
                              className={`bi bi-chevron-${expandedMenus.transactions ? 'down' : 'right'} nav-chevron`}
                            ></i>
                          </div>
                          {expandedMenus.transactions && (
                            <div className="nav-submenu">
                              <NavPermissionLink
                                to="/transactions/sales"
                                className={`nav-link-modern nav-submenu-item ${isActive('/transactions/sales') ? 'active' : ''}`}
                              >
                                <div className="nav-icon">
                                  <i className="bi bi-cart-check"></i>
                                </div>
                                <span className="nav-text">
                                  {isManufacturer
                                    ? 'Sales to distributors'
                                    : 'Sales to outlets'}
                                </span>
                              </NavPermissionLink>

                              <NavPermissionLink
                                to="/transactions/purchase"
                                className={`nav-link-modern nav-submenu-item ${isActive('/transactions/purchase') ? 'active' : ''}`}
                              >
                                <div className="nav-icon">
                                  <i className="bi bi-cart-plus"></i>
                                </div>
                                <span className="nav-text">
                                  {isManufacturer
                                    ? 'Purchase'
                                    : 'Purchase from supplier'}
                                </span>
                              </NavPermissionLink>

                              <NavPermissionLink
                                to="/transactions/receipt"
                                className={`nav-link-modern nav-submenu-item ${isActive('/transactions/receipt') ? 'active' : ''}`}
                              >
                                <div className="nav-icon">
                                  <i className="bi bi-arrow-down-circle"></i>
                                </div>
                                <span className="nav-text">Receipt</span>
                              </NavPermissionLink>

                              <NavPermissionLink
                                to="/transactions/payment"
                                className={`nav-link-modern nav-submenu-item ${isActive('/transactions/payment') ? 'active' : ''}`}
                              >
                                <div className="nav-icon">
                                  <i className="bi bi-arrow-up-circle"></i>
                                </div>
                                <span className="nav-text">Payment</span>
                              </NavPermissionLink>
                            </div>
                          )}
                        </div>

                        {!isDistributor && (
                          <NavPermissionLink
                            to="/sales/distributors"
                            className={`nav-link-modern nav-submenu-item ${isActive('/sales/distributors') ? 'active' : ''}`}
                          >
                            <div className="nav-icon">
                              <i className="bi bi-shop"></i>
                            </div>
                            <span className="nav-text">Sales</span>
                          </NavPermissionLink>
                        )}

                        <NavPermissionLink
                          to="/skus-v2"
                          className={`nav-link-modern nav-submenu-item ${isActive('/skus-v2') || isActive('/tally/master/skus') ? 'active' : ''}`}
                        >
                          <div className="nav-icon">
                            <i className="bi bi-tags"></i>
                          </div>
                          <span className="nav-text">SKUs</span>
                        </NavPermissionLink>
                        <NavPermissionLink
                          to="/tally-sync"
                          className={`nav-link-modern nav-submenu-item ${isActive('/tally-sync') ? 'active' : ''}`}
                        >
                          <div className="nav-icon">
                            <i className="bi bi-gear"></i>
                          </div>
                          <span className="nav-text">Tally Sync</span>
                        </NavPermissionLink>
                      </div>
                    )}
                  </div> */}
                  {/* Tally Reports v2 Dropdown */}
                  <div className="nav-group">
                    <div
                      className={`nav-link-modern nav-group-toggle ${isSidebarCollapsed ? (isTallyReportsV2Active ? 'active' : '') : (expandedMenus.tallyReportsV2 || isTallyReportsV2Active ? 'active' : '')}`}
                      onClick={() => isSidebarCollapsed ? (navigate(isManufacturer ? '/mfg-dashboard' : '/tally-dashboard-v2'), handleNavLinkClick()) : toggleMenu('tallyReportsV2')}
                      data-tooltip="Analytics"
                    >
                      <div className="nav-icon">
                        <img
                          src={TallyIcon}
                          alt=""
                          className="nav-icon-standard"
                        />
                      </div>
                      <span className="nav-text">Analytics</span>
                      <i
                        className={`bi bi-chevron-${expandedMenus.tallyReportsV2 ? 'down' : 'right'} nav-chevron`}
                      ></i>
                    </div>
                    {expandedMenus.tallyReportsV2 && (
                      <div className="nav-submenu">
                        {/* Dashboard Nested Dropdown */}
                        <div className="nav-group">
                          <div
                            className={`nav-link-modern nav-group-toggle nav-submenu-item ${isActive('/tally-dashboard-v2') || isActive('/mfg-dashboard') || isActive('/dashboard/receivables-v2') || isActive('/dashboard/payables-v2') || isActive('/transactions/sales-v2') || isActive('/sales/distributors') || isActive('/sales/distributors2') || isActive('/opsc-report') || isActive('/opsc') ? 'has-active-child' : ''}`}
                          >
                            <div
                              className="nav-link-content"
                              onClick={e => {
                                e.stopPropagation();
                                navigate(isManufacturer ? '/mfg-dashboard' : '/tally-dashboard-v2');
                                handleNavLinkClick();
                              }}
                              style={{ display: 'flex', alignItems: 'center', flex: 1, cursor: 'pointer' }}
                            >
                              <div className="nav-icon">
                                <i className="bi bi-bar-chart"></i>
                              </div>
                              <span className="nav-text">Dashboard</span>
                            </div>
                            <i
                              className={`bi bi-chevron-${expandedMenus.tallyDashboard ? 'down' : 'right'} nav-chevron`}
                              onClick={e => {
                                e.stopPropagation();
                                toggleSubMenu('tallyDashboard');
                              }}
                              style={{ cursor: 'pointer' }}
                            ></i>
                          </div>
                          {expandedMenus.tallyDashboard && (
                            <div className="nav-submenu">
                              {/* Primary Sales - Sales to Distributors (Manufacturers) or Sales to Outlets (Distributors) */}
                              {/* {isManufacturer && showPrimarySalesSetting && (
                                <NavPermissionLink
                                  to="/transactions/sales-v2"
                                  data-tour="sales2-primary-tab"
                                  className={`nav-link-modern nav-submenu-item ${isActive('/transactions/sales-v2') ? 'active' : ''}`}
                                  onClick={handleNavLinkClick}
                                >
                                  <div className="nav-icon">
                                    <img
                                      src={secondarySalesIcon}
                                      alt=""
                                      className="nav-icon-standard"
                                    />
                                  </div>
                                  <span className="nav-text">
                                    {isManufacturer ? 'Purchase' : 'Primary Sales'}
                                  </span>
                                </NavPermissionLink>
                              )} */}

                              {/* Party x SKU Deep Dive (mobile) */}
                              {/* {isManufacturer && showPrimarySalesSetting && (
                                <NavPermissionLink
                                  to="/dashboard/party-sku-deep-dive"
                                  className={`nav-link-modern nav-submenu-item ${isActive('/dashboard/party-sku-deep-dive') ? 'active' : ''}`}
                                  onClick={handleNavLinkClick}
                                >
                                  <div className="nav-icon">
                                    <img
                                      src={secondarySalesIcon}
                                      alt=""
                                      className="nav-icon-standard"
                                    />
                                  </div>
                                  <span className="nav-text">Party × SKU Deep Dive</span>
                                </NavPermissionLink>
                              )} */}

                              {isDistributor && showPrimarySalesSetting && (
                                <NavPermissionLink
                                  to="/transactions/sales-v2"
                                  data-tour="sales2-primary-tab"
                                  className={`nav-link-modern nav-submenu-item ${isActive('/transactions/sales-v2') ? 'active' : ''}`}
                                  onClick={handleNavLinkClick}
                                >
                                  <div className="nav-icon">
                                    <img
                                      src={secondarySalesIcon}
                                      alt=""
                                      className="nav-icon-standard"
                                    />
                                  </div>
                                  <span className="nav-text">
                                    Sales to outlets
                                  </span>
                                </NavPermissionLink>
                              )}

                              {/* Secondary Sales */}
                              {isManufacturer && (
                                <NavPermissionLink
                                  to="/sales/distributors-v2"
                                  className={`nav-link-modern nav-submenu-item ${isActive('/sales/distributors-v2') ? 'active' : ''}`}
                                  onClick={handleNavLinkClick}
                                >
                                  <div className="nav-icon">
                                    <img
                                      src={secondarySalesIcon}
                                      alt=""
                                      className="nav-icon-standard"
                                    />
                                  </div>
                                  <span className="nav-text">Sales</span>
                                </NavPermissionLink>
                              )}

                              {isManufacturer && (
                                <NavPermissionLink
                                  to="/opsc-report"
                                  className={`nav-link-modern nav-submenu-item ${isActive('/opsc-report') || isActive('/opsc') ? 'active' : ''}`}
                                  onClick={handleNavLinkClick}
                                >
                                  <div className="nav-icon">
                                    <i className="bi bi-file-earmark-spreadsheet" style={{ fontSize: '18px' }}></i>
                                  </div>
                                  <span className="nav-text">OPSC Report</span>
                                </NavPermissionLink>
                              )}

                              {/* Bank and Cash */}
                              {/* {showPrimarySalesSetting && (
                                <NavPermissionLink
                                  to="/dashboard/bank-and-cash"
                                  className={`nav-link-modern nav-submenu-item ${isActive('/dashboard/bank-and-cash') ? 'active' : ''}`}
                                  onClick={handleNavLinkClick}
                                >
                                  <div className="nav-icon">
                                    <img
                                      src={bankCashIcon}
                                      alt=""
                                      className="nav-icon-standard"
                                    />
                                  </div>
                                  <span className="nav-text">Bank and Cash</span>
                                </NavPermissionLink>
                              )} */}

                              {/* Receivables - Show for both Manufacturer and Distributor */}
                              {!isManufacturer && showPrimarySalesSetting && (
                                <NavPermissionLink
                                  to="/dashboard/receivables-v2"
                                  className={`nav-link-modern nav-submenu-item ${isActive('/dashboard/receivables-v2') ? 'active' : ''}`}
                                  onClick={handleNavLinkClick}
                                >
                                  <div className="nav-icon">
                                    <img
                                      src={receivablesIconNew}
                                      alt=""
                                      className="nav-icon-standard"
                                    />
                                  </div>
                                  <span className="nav-text">Receivables</span>
                                </NavPermissionLink>
                              )}

                              {/* Payables - Show only for Distributor */}
                              {isDistributor && showPrimarySalesSetting && (
                                <NavPermissionLink
                                  to="/dashboard/payables-v2"
                                  className={`nav-link-modern nav-submenu-item ${isActive('/dashboard/payables-v2') ? 'active' : ''}`}
                                  onClick={handleNavLinkClick}
                                >
                                  <div className="nav-icon">
                                    <img
                                      src={payablesIconNew}
                                      alt=""
                                      className="nav-icon-standard"
                                    />
                                  </div>
                                  <span className="nav-text">Payables</span>
                                </NavPermissionLink>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Transactions v2 Nested Dropdown */}
                        {!isManufacturer && (
                          <div className="nav-group">
                            <div
                              className={`nav-link-modern nav-group-toggle nav-submenu-item ${isActive('/transactions/sales-v2') || isActive('/transactions/purchase-v2') || isActive('/transactions/receipt-v2') || isActive('/transactions/credit-note-v2') || isActive('/transactions/debit-note-v2') || isActive('/transactions/generals-v2') || isActive('/transactions/payment-v2') || isActive('/invoices-v2') ? 'has-active-child' : ''}`}
                              onClick={e => {
                                e.stopPropagation();
                                toggleSubMenu('transactionsV2');
                              }}
                            >
                              <div className="nav-icon">
                                <img
                                  src={transactionIcon}
                                  alt=""
                                  className="nav-icon-standard"
                                />
                              </div>
                              <span className="nav-text">Transactions</span>
                              <i
                                className={`bi bi-chevron-${expandedMenus.transactionsV2 ? 'down' : 'right'} nav-chevron`}
                              ></i>
                            </div>
                            {expandedMenus.transactionsV2 && (
                              <div className="nav-submenu">
                                {!isManufacturer && (
                                  <NavPermissionLink
                                    to="/transactions/purchase-v2"
                                    className={`nav-link-modern nav-submenu-item ${isActive('/transactions/purchase-v2') ? 'active' : ''}`}
                                    onClick={handleNavLinkClick}
                                  >
                                    <div className="nav-icon">
                                      <i className="bi bi-cart-plus"></i>
                                    </div>
                                    <span className="nav-text">Purchase from supplier</span>
                                  </NavPermissionLink>
                                )}

                                <NavPermissionLink
                                  to="/transactions/receipt-v2"
                                  className={`nav-link-modern nav-submenu-item ${isActive('/transactions/receipt-v2') ? 'active' : ''}`}
                                  onClick={handleNavLinkClick}
                                >
                                  <div className="nav-icon">
                                    <i className="bi bi-arrow-down-circle"></i>
                                  </div>
                                  <span className="nav-text">Receipt</span>
                                </NavPermissionLink>

                                <NavPermissionLink
                                  to="/transactions/credit-note-v2"
                                  className={`nav-link-modern nav-submenu-item ${isActive('/transactions/credit-note-v2') ? 'active' : ''}`}
                                  onClick={handleNavLinkClick}
                                >
                                  <div className="nav-icon">
                                    <i className="bi bi-file-earmark-plus"></i>
                                  </div>
                                  <span className="nav-text">Credit Note</span>
                                </NavPermissionLink>

                                <NavPermissionLink
                                  to="/transactions/debit-note-v2"
                                  className={`nav-link-modern nav-submenu-item ${isActive('/transactions/debit-note-v2') ? 'active' : ''}`}
                                  onClick={handleNavLinkClick}
                                >
                                  <div className="nav-icon">
                                    <i className="bi bi-file-earmark-minus"></i>
                                  </div>
                                  <span className="nav-text">Debit Note</span>
                                </NavPermissionLink>

                                <NavPermissionLink
                                  to="/transactions/generals-v2"
                                  className={`nav-link-modern nav-submenu-item ${isActive('/transactions/generals-v2') ? 'active' : ''}`}
                                  onClick={handleNavLinkClick}
                                >
                                  <div className="nav-icon">
                                    <i className="bi bi-journal-text"></i>
                                  </div>
                                  <span className="nav-text">Journal Voucher</span>
                                </NavPermissionLink>

                                {!isManufacturer && (
                                  <NavPermissionLink
                                    to="/transactions/payment-v2"
                                    className={`nav-link-modern nav-submenu-item ${isActive('/transactions/payment-v2') ? 'active' : ''}`}
                                    onClick={handleNavLinkClick}
                                  >
                                    <div className="nav-icon">
                                      <i className="bi bi-arrow-up-circle"></i>
                                    </div>
                                    <span className="nav-text">Payment</span>
                                  </NavPermissionLink>
                                )}

                                {!isManufacturer && (
                                  <NavPermissionLink
                                    to="/invoices-v2"
                                    className={`nav-link-modern nav-submenu-item ${isActive('/invoices-v2') ? 'active' : ''}`}
                                    onClick={handleNavLinkClick}
                                  >
                                    <div className="nav-icon">
                                      <i className="bi bi-file-earmark-text"></i>
                                    </div>
                                    <span className="nav-text">Invoices</span>
                                  </NavPermissionLink>
                                )}

                              </div>
                            )}
                          </div>
                        )}

                        {/* My SKUs - For Manufacturers and Distributors */}
                        {(isDistributor || isManufacturer) && (
                          <NavPermissionLink
                            to="/skus-v2"
                            data-tour="sku2-sidebar-link-mobile"
                            className={`nav-link-modern nav-submenu-item ${isActive('/skus-v2') ? 'active' : ''}`}
                            onClick={handleNavLinkClick}
                          >
                            <div className="nav-icon">
                              <img
                                src={skusIcon}
                                alt=""
                                className="nav-icon-standard"
                              />
                            </div>
                            <span className="nav-text">My SKUs</span>
                          </NavPermissionLink>
                        )}
                      </div>
                    )}
                  </div>
                  {/* SKU Mapping V2 Menu - Only for Manufacturer and Distributor */}
                  {(isManufacturer || isDistributor) && (
                    <div className="nav-group">
                      <div
                        className={`nav-link-modern nav-group-toggle ${isSidebarCollapsed ? (isSkuMappingV2Active ? 'active' : '') : (expandedMenus.skuMappingV2 || isSkuMappingV2Active ? 'active' : '')}`}
                        onClick={() => isSidebarCollapsed ? (navigate(isManufacturer ? '/secondary-sales/sku-sales-v2' : '/secondary-sales/requests-v2'), handleNavLinkClick()) : toggleMenu('skuMappingV2')}
                        data-tooltip="SKU Mapping"
                      >
                        <div className="nav-icon">
                          <img
                            src={skuMappingIcon}
                            alt=""
                            className="nav-icon-standard"
                          />
                        </div>
                        <span className="nav-text">SKU Mapping</span>
                        <i
                          className={`bi bi-chevron-${expandedMenus.skuMappingV2 ? 'down' : 'right'} nav-chevron`}
                        ></i>
                      </div>
                      {expandedMenus.skuMappingV2 && (
                        <div className="nav-submenu">
                          {isManufacturer && (
                            <>
                              <NavPermissionLink
                                to="/secondary-sales/sku-sales-v2"
                                className={`nav-link-modern nav-submenu-item ${isActive('/secondary-sales/sku-sales-v2') ? 'active' : ''}`}
                                onClick={handleNavLinkClick}
                              >
                                <div className="nav-icon">
                                  <img
                                    src={skuSaleIcon}
                                    alt=""
                                    className="nav-icon-standard"
                                  />
                                </div>
                                <span className="nav-text">SKUs to map</span>
                              </NavPermissionLink>
                              <NavPermissionLink
                                to="/secondary-sales/send-request-v2"
                                className={`nav-link-modern nav-submenu-item ${isActive('/secondary-sales/send-request-v2') ? 'active' : ''}`}
                                onClick={handleNavLinkClick}
                              >
                                <div className="nav-icon">
                                  <img
                                    src={sendRequestIcon}
                                    alt=""
                                    className="nav-icon-standard"
                                  />
                                </div>
                                <span className="nav-text">Send Request</span>
                              </NavPermissionLink>
                              <NavPermissionLink
                                to="/secondary-sales/my-requests-v2"
                                className={`nav-link-modern nav-submenu-item ${isActive('/secondary-sales/my-requests-v2') ? 'active' : ''}`}
                                onClick={handleNavLinkClick}
                              >
                                <div className="nav-icon">
                                  <img
                                    src={myRequestIcon}
                                    alt=""
                                    className="nav-icon-standard"
                                  />
                                </div>
                                <span className="nav-text">My Requests</span>
                              </NavPermissionLink>
                              {/* <NavPermissionLink
                                to="/secondary-sales/mapping-logs-v2"
                                className={`nav-link-modern nav-submenu-item ${isActive('/secondary-sales/mapping-logs-v2') ? 'active' : ''}`}
                                onClick={handleNavLinkClick}
                              >
                                <div className="nav-icon">
                                  <img
                                    src={myRequestIcon}
                                    alt=""
                                    className="nav-icon-standard"
                                  />
                                </div>
                                <span className="nav-text">Mapping Logs</span>
                              </NavPermissionLink> */}
                            </>
                          )}
                          {isDistributor && (
                            <>
                              <NavPermissionLink
                                to="/secondary-sales/requests-v2"
                                className={`nav-link-modern nav-submenu-item ${isActive('/secondary-sales/requests-v2') ? 'active' : ''}`}
                                onClick={handleNavLinkClick}
                              >
                                <div className="nav-icon">
                                  <img
                                    src={myRequestIcon}
                                    alt=""
                                    className="nav-icon-standard"
                                  />
                                </div>
                                <span className="nav-text">All Requests</span>
                              </NavPermissionLink>
                              {/* <NavPermissionLink
                                to="/skus-v2"
                                className={`nav-link-modern nav-submenu-item ${isActive('/skus-v2') ? 'active' : ''}`}
                                onClick={handleNavLinkClick}
                              >
                                <div className="nav-icon">
                                  <img
                                    src={myRequestIcon}
                                    alt=""
                                    className="nav-icon-standard"
                                  />
                                </div>
                                <span className="nav-text">SKUs</span>
                              </NavPermissionLink> */}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Independent My SKUs link for Manufacturers */}

                  {/* SKU Mapping Menu - COMMENTED OUT */}
                  {/* {(isManufacturer || isDistributor) && (
                    <div className="nav-group">
                      <div
                        className={`nav-link-modern nav-group-toggle ${expandedMenus.skuMapping ? 'active' : ''}`}
                        onClick={() => toggleMenu('skuMapping')}
                      >
                        <div className="nav-icon">
                          <i className="bi bi-link-45deg"></i>
                        </div>
                        <span className="nav-text">SKU Mapping</span>
                        <i
                          className={`bi bi-chevron-${expandedMenus.skuMapping ? 'down' : 'right'} nav-chevron`}
                        ></i>
                      </div>
                      {expandedMenus.skuMapping && (
                        <div className="nav-submenu">
                          {isManufacturer && (
                            <>
                              <NavPermissionLink
                                to="/secondary-sales/send-request-v2"
                                className={`nav-link-modern nav-submenu-item ${isActive('/secondary-sales/send-request-v2') ? 'active' : ''}`}
                                onClick={handleNavLinkClick}
                              >
                                <div className="nav-icon">
                                  <i className="bi bi-send"></i>
                                </div>
                                <span className="nav-text">Send Request</span>
                              </NavPermissionLink>

                              <NavPermissionLink
                                to="/secondary-sales/my-requests-v2"
                                className={`nav-link-modern nav-submenu-item ${isActive('/secondary-sales/my-requests-v2') ? 'active' : ''}`}
                                onClick={handleNavLinkClick}
                              >
                                <div className="nav-icon">
                                  <i className="bi bi-list-ul"></i>
                                </div>
                                <span className="nav-text">My Requests</span>
                              </NavPermissionLink>

                              <NavPermissionLink
                                to="/secondary-sales/sku-sales-v2"
                                className={`nav-link-modern nav-submenu-item ${isActive('/secondary-sales/sku-sales-v2') ? 'active' : ''}`}
                                onClick={handleNavLinkClick}
                              >
                                <div className="nav-icon">
                                  <i className="bi bi-check2-square"></i>
                                </div>
                                <span className="nav-text">SKUs to map</span>
                              </NavPermissionLink>
                            </>
                          )}

                          {isDistributor && (
                            <NavPermissionLink
                              to="/secondary-sales/requests"
                              className={`nav-link-modern nav-submenu-item ${isActive('/secondary-sales/requests') ? 'active' : ''}`}
                              onClick={handleNavLinkClick}
                            >
                              <div className="nav-icon">
                                <i className="bi bi-inbox"></i>
                              </div>
                              <span className="nav-text">Mapping Requests</span>
                            </NavPermissionLink>
                          )}
                        </div>
                      )}
                    </div>
                  )} */}

                  {/* Receivable Link - Only for Manufacturer and Distributor */}
                  {/* {(isManufacturer || isDistributor) && (
                    <NavPermissionLink
                      to="/receivable"
                      className={`nav-link-modern ${isActive('/receivable') ? 'active' : ''}`}
                      onClick={handleNavLinkClick}
                    >
                      <div className="nav-icon">
                        <i className="bi bi-receipt"></i>
                      </div>
                      <span className="nav-text">Receivable</span>
                    </NavPermissionLink>
                  )} */}

                  {/* Payable Link - Only for Manufacturer and Distributor */}
                  {/* {(isManufacturer || isDistributor) && (
                    <NavPermissionLink
                      to="/payable"
                      className={`nav-link-modern ${isActive('/payable') ? 'active' : ''}`}
                      onClick={handleNavLinkClick}
                    >
                      <div className="nav-icon">
                        <i className="bi bi-credit-card"></i>
                      </div>
                      <span className="nav-text">Payable</span>
                    </NavPermissionLink>
                  )} */}

                  {/* Business Structure Menu */}
                  <div className="nav-group">
                    <div
                      className={`nav-link-modern nav-group-toggle ${isSidebarCollapsed ? (isBusinessStructureActive ? 'active' : '') : (expandedMenus.businessStructure || isBusinessStructureActive ? 'active' : '')}`}
                      onClick={() => isSidebarCollapsed ? (navigate('/sales-mapping'), handleNavLinkClick()) : toggleMenu('businessStructure')}
                      data-tooltip="Business Structure"
                    >
                      <div className="nav-icon">
                        <img
                          src={BusinessStructureIcon}
                          alt=""
                          className="nav-icon-standard"
                        />
                      </div>
                      <span className="nav-text">Business Structure</span>
                      <i
                        className={`bi bi-chevron-${expandedMenus.businessStructure ? 'down' : 'right'} nav-chevron`}
                      ></i>
                    </div>
                    {expandedMenus.businessStructure && (
                      <div className="nav-submenu">
                        <NavPermissionLink
                          to={isManufacturer ? "/distributor-staff-mapping" : "/sales-mapping"}
                          data-tour="sales-mapping-sidebar-link"
                          className={`nav-link-modern nav-submenu-item ${isActive(isManufacturer ? '/distributor-staff-mapping' : '/sales-mapping') ? 'active' : ''}`}
                          onClick={handleNavLinkClick}
                        >
                          <div className="nav-icon">
                            <img
                              src={SalesTeamMappingIcon}
                              alt=""
                              className="nav-icon-standard"
                            />
                          </div>
                          <span className="nav-text">
                            {isDistributor ? 'Outlet Staff Mapping' : 'Distributor Staff Mapping'}
                          </span>
                        </NavPermissionLink>

                        <NavPermissionLink
                          to="/industry-categories"
                          className={`nav-link-modern nav-submenu-item ${isActive('/industry-categories') ? 'active' : ''}`}
                          onClick={handleNavLinkClick}
                        >
                          <div className="nav-icon">
                            <i className="bi bi-grid-3x3-gap"></i>
                          </div>
                          <span className="nav-text">Category/Subcategory</span>
                        </NavPermissionLink>
                      </div>
                    )}
                  </div>

                  {/* User Management Menu */}
                  <div className="nav-group">
                    <div
                      className={`nav-link-modern nav-group-toggle ${isSidebarCollapsed ? (isUserManagementActive ? 'active' : '') : (expandedMenus.userManagement || isUserManagementActive ? 'active' : '')}`}
                      onClick={() => isSidebarCollapsed ? (navigate('/user-role-management'), handleNavLinkClick()) : toggleMenu('userManagement')}
                      data-tour="usermgmt-nav-toggle"
                      data-tooltip="User Management"
                    >
                      <div className="nav-icon">
                        <i className="bi bi-people"></i>
                      </div>
                      <span className="nav-text">User Management</span>
                      <i
                        className={`bi bi-chevron-${expandedMenus.userManagement ? 'down' : 'right'} nav-chevron`}
                      ></i>
                    </div>
                    {expandedMenus.userManagement && (
                      <div className="nav-submenu">
                        <NavPermissionLink
                          to="/user-role-management"
                          className={`nav-link-modern nav-submenu-item ${location.pathname === '/user-role-management' ? 'active' : ''}`}
                          onClick={handleNavLinkClick}
                          data-tour="usermgmt-nav-listings"
                        >
                          <div className="nav-icon">
                            <i className="bi bi-people"></i>
                          </div>
                          <span className="nav-text">Users Listing</span>
                        </NavPermissionLink>

                        <NavPermissionLink
                          to="/user-role-management/hierarchy"
                          className={`nav-link-modern nav-submenu-item ${isActive('/user-role-management/hierarchy') ? 'active' : ''}`}
                          onClick={handleNavLinkClick}
                        >
                          <div className="nav-icon">
                            <i className="bi bi-diagram-3"></i>
                          </div>
                          <span className="nav-text">Employee Hierarchy</span>
                        </NavPermissionLink>

                        <NavPermissionLink
                          to="/roles-and-permissions"
                          className={`nav-link-modern nav-submenu-item ${isActive('/roles-and-permissions') ? 'active' : ''}`}
                          onClick={handleNavLinkClick}
                        >
                          <div className="nav-icon">
                            <img
                              src={RolesPermissionIcon}
                              alt=""
                              className="nav-icon-standard"
                            />
                          </div>
                          <span className="nav-text">Roles & Permissions</span>
                        </NavPermissionLink>

                        <NavPermissionLink
                          to="/labels"
                          className={`nav-link-modern nav-submenu-item ${isActive('/labels') ? 'active' : ''}`}
                          onClick={handleNavLinkClick}
                        >
                          <div className="nav-icon">
                            <i className="bi bi-people-fill"></i>
                          </div>
                          <span className="nav-text">Teams</span>
                        </NavPermissionLink>
                      </div>
                    )}
                  </div>
                  {/* Tally Sync */}
                  <NavPermissionLink
                    to="/tally-sync"
                    data-tooltip="Company settings"
                    className={`nav-link-modern ${isActive('/tally-sync') ? 'active' : ''}`}
                    onClick={handleNavLinkClick}
                  >
                    <div className="nav-icon">
                      <i className="bi bi-arrow-repeat"></i>
                    </div>
                    <span className="nav-text">Company settings</span>
                  </NavPermissionLink>
                  {isManufacturer && (
                    <NavPermissionLink
                      to="/mfg-sync-status"
                      className={`nav-link-modern ${isActive('/mfg-sync-status') ? 'active' : ''}`}
                      onClick={handleNavLinkClick}
                    >
                      <div className="nav-icon">
                        <i className="bi bi-activity"></i>
                      </div>
                      <span className="nav-text">Sync Status</span>
                    </NavPermissionLink>
                  )}

                  {isManufacturer && (
                    <NavPermissionLink
                      unrestricted
                      to="/installations"
                      className={`nav-link-modern ${isActive('/installations') ? 'active' : ''}`}
                      onClick={handleNavLinkClick}
                      data-tooltip="Installations"
                    >
                      <div className="nav-icon">
                        <i className="bi bi-tools"></i>
                      </div>
                      <span className="nav-text">Installations</span>
                    </NavPermissionLink>
                  )}

                  {isManufacturer && (
                    <NavPermissionLink
                      unrestricted
                      to="/not-onboarding-leads"
                      className={`nav-link-modern ${isActive('/not-onboarding-leads') ? 'active' : ''}`}
                      onClick={handleNavLinkClick}
                      data-tooltip="Not Onboarding"
                    >
                      <div className="nav-icon">
                        <i className="bi bi-person-x"></i>
                      </div>
                      <span className="nav-text">Not Onboarding</span>
                    </NavPermissionLink>
                  )}

                  {isManufacturer && (
                    <NavPermissionLink
                      to="/dump-report"
                      className={`nav-link-modern ${isActive('/dump-report') ? 'active' : ''}`}
                      onClick={handleNavLinkClick}
                      data-tooltip="Dump Report"
                    >
                      <div className="nav-icon">
                        <img
                          src={transactionIcon}
                          alt=""
                          className="nav-icon-standard"
                        />
                      </div>
                      <span className="nav-text">Dump Report</span>
                    </NavPermissionLink>
                  )}

                  {/* Profile Menu */}
                  <div className="nav-group">
                    <div
                      className={`nav-link-modern nav-group-toggle ${isSidebarCollapsed ? (isProfileActive ? 'active' : '') : (expandedMenus.profile || isProfileActive ? 'active' : '')}`}
                      onClick={() => isSidebarCollapsed ? (navigate('/my-profile'), handleNavLinkClick()) : toggleMenu('profile')}
                      data-tooltip="Profile"
                    >
                      <div className="nav-icon">
                        <img
                          src={ProfileIcon}
                          alt=""
                          className="nav-icon-standard"
                        />
                      </div>
                      <span className="nav-text">Profile</span>
                      <i
                        className={`bi bi-chevron-${expandedMenus.profile ? 'down' : 'right'} nav-chevron`}
                      ></i>
                    </div>
                    {expandedMenus.profile && (
                      <div className="nav-submenu">
                        <NavPermissionLink
                          unrestricted
                          to="/my-profile"
                          className={`nav-link-modern nav-submenu-item ${isActive('/my-profile') ? 'active' : ''}`}
                          onClick={handleNavLinkClick}
                        >
                          <div className="nav-icon">
                            <i className="bi bi-person-badge"></i>
                          </div>
                          <span className="nav-text">My Profile</span>
                        </NavPermissionLink>

                        {/* <NavPermissionLink
                          unrestricted
                          to="/saved-profiles"
                          className={`nav-link-modern nav-submenu-item ${isActive('/saved-profiles') ? 'active' : ''}`}
                          onClick={handleNavLinkClick}
                        >
                          <div className="nav-icon">
                            <i className="bi bi-bookmark"></i>
                          </div>
                          <span className="nav-text">View Saved Profiles</span>
                        </NavPermissionLink> */}
                      </div>
                    )}
                  </div>

                  {hasCompanyRoleAssignment && (
                    <div className="nav-group spp-nav-group">
                      <div
                        className="nav-link-modern nav-group-toggle spp-profile-trigger"
                        onClick={() => setShowSalesProfilePopover(v => !v)}
                      >
                        <div className="spp-trigger-avatar">
                          {user?.user_name
                            ? (() => {
                              const parts = user.user_name.trim().split(' ');
                              return parts.length >= 2
                                ? (parts[0][0] + parts[1][0]).toUpperCase()
                                : user.user_name.slice(0, 2).toUpperCase();
                            })()
                            : 'U'}
                        </div>
                        <div className="spp-trigger-info">
                          <span className="spp-trigger-name">
                            {user?.user_name || 'User'}
                          </span>
                          <span className="spp-trigger-role">
                            {user?.companyRole?.role_name ||
                              user?.company_role?.role_name ||
                              user?.role_name ||
                              ''}
                          </span>
                        </div>
                      </div>
                      {showSalesProfilePopover && (
                        <div className="spp-popover-wrapper">
                          <SalesProfilePopover
                            onClose={() => setShowSalesProfilePopover(false)}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </nav>

        {/* COMMENTED OUT: Hide upload product and add requirement buttons
        <div className="sidebar-hero-card">
          <div className={`sidebar-hero-card__image-wrapper${isSalesOnlySidebar ? ' spp-hidden' : ''}`}>
            <img
              src={HeroImage}
              alt="Next steps illustration"
              className="sidebar-hero-card__image sidebar-hero-image"
            />
          </div>
          <div className="sidebar-hero-card__body">
            {isManufacturer && (
              <button
                type="button"
                className="upload-products-btn"
                onClick={() => navigate('/products')}
              >
                Upload product
              </button>
            )}
            {isDistributor && (
              <button
                type="button"
                className="upload-products-btn"
                onClick={() => navigate('/requirements')}
              >
                Add requirement
              </button>
            )}
          </div>
        </div>
        */}

        <button
          type="button"
          className="sidebar-logout-btn"
          onClick={handleLogout}
          data-tooltip="Logout"
        >
          <img src={LogoutIcon} alt="Logout" className="logout-icon-standard" />
          <span>Logout</span>
        </button>
      </aside>

      <div className="admin-main-content">
        {/* Header */}
        <header className="modern-header">
          <div className="admin-header-container">
            <div className="admin-header-left">
              <button
                className="admin-sidebar-toggle-btn"
                onClick={() => setSidebarOpen(v => !v)}
                aria-label="Toggle sidebar"
              >
                <img
                  src={MenuSlashIcon}
                  alt="Menu"
                  className="header-icon-standard"
                />
              </button>

              {/* 
              <button
                className="admin-mobile-search-btn"
                onClick={() => setMobileSearchOpen(v => !v)}
                aria-label="Search"
              >
                <img
                  src={SearchIcon}
                  alt="Search"
                  className="header-icon-standard"
                />
              </button>
              */}

              {/* 
              <div className="admin-header-search-wrapper">
                <SimpleSearchBar
                  placeholder="Search by brand, category, or city..."
                  onSearch={handleSearch}
                  showSearchButton={true}
                  buttonLabel="Search"
                  showDropdown={true}
                  className="dashboard-search-bar"
                />
              </div>
              */}
            </div>

            <div className="admin-header-right">
              {/* Quick Guide button removed — tours now start automatically on first visit */}

              {/* {showBusinessFinancialToggle && (
                <div data-tour="dashboard-toggle" className="manufacturer-dashboard-buttons">
                  <Button
                    variant={
                      activeDashboard === 'financial'
                        ? 'brand'
                        : 'outline-secondary'
                    }
                    size="sm"
                    onClick={() => {
                      setActiveDashboard('financial');
                      // Navigate sales users to Tally Dashboard V2, others to Financial Dashboard
                      navigate(userRole?.role_name === 'sales_user' ? (isManufacturer ? '/mfg-dashboard' : '/tally-dashboard-v2') : '/financial-dashboard');
                    }}
                    className="manufacturer-dashboard-btn"
                  >
                    Financial Dashboard
                  </Button>
                  { 
                  <Button
                    variant={
                      activeDashboard === 'business'
                        ? 'brand'
                        : 'outline-secondary'
                    }
                    size="sm"
                    onClick={() => {
                      setActiveDashboard('business');
                      navigate('/dashboard');
                    }}
                    className="manufacturer-dashboard-btn"
                  >
                    Business Dashboard
                  </Button>
                  }
                </div>
              )} */}

              <div className="admin-header-icons">
                {isDistributor && !isSalesOnlySidebar && isSyncStale && (
                  <div
                    className="stale-sync-badge"
                    tabIndex={0}
                    title={`Data Not synced since ${new Date(lastsyncDate).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}. Please sync.`}
                    onClick={() => navigate('/tally-sync')}
                  >
                    <i className="bi bi-exclamation-triangle-fill stale-sync-icon"></i>
                    <div className="stale-sync-marquee">
                      <span className="stale-sync-text">
                        Data Not synced since {new Date(lastsyncDate).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </span>
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  className="header-support-btn"
                  title="Support — report an issue"
                  onClick={() =>
                    navigate('/support', {
                      state: location.pathname.startsWith('/support')
                        ? undefined
                        : { from: `${location.pathname}${location.search}`, raise: true },
                    })
                  }
                >
                  <i className="bi bi-life-preserver"></i>
                  <span>Support</span>
                </button>
                <NotificationDropdown userId={user?.id} />

                <div className="admin-company-info-section">
                  {companyData?.company_name && (
                    <div className="admin-company-name admin-company-name--full">
                      {companyData.company_name}
                    </div>
                  )}
                  {companyData?.company_name && (
                    <div className="admin-company-initials-badge">
                      {companyData.company_name
                        .trim()
                        .split(/\s+/)
                        .slice(0, 2)
                        .map(w => w[0]?.toUpperCase() ?? '')
                        .join('')}
                    </div>
                  )}
                  <div className="admin-company-info-badges">
                    {companyData?.role && (
                      <div className="admin-company-role-badge">
                        <span className="admin-role-full">
                          {companyData.role.charAt(0).toUpperCase() + companyData.role.slice(1)}
                        </span>
                        <span className="admin-role-initial">
                          {companyData.role.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    {companyData?.id && (
                      <div className="admin-company-id-badge">
                        <span className="admin-company-id-text">ID: {companyData.id}</span>
                        <button
                          type="button"
                          className="admin-company-id-copy-btn"
                          title={copiedId ? 'Copied!' : 'Copy ID'}
                          onClick={() => {
                            navigator.clipboard.writeText(String(companyData.id)).then(() => {
                              setCopiedId(true);
                              setTimeout(() => setCopiedId(false), 2000);
                            });
                          }}
                          aria-label="Copy company ID"
                        >
                          {copiedId ? (
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <path d="M2 7l3.5 3.5L12 3" stroke="#7839CD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          ) : (
                            <img src={copyIcon} alt="Copy" className="admin-company-id-copy-icon" />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* <div
                  className="admin-icon-shell admin-language-wrapper position-relative"
                  ref={languageMenuRef}
                >
                  <button
                    type="button"
                    className={`admin-icon-shell__btn ${languageMenuOpen ? 'is-active' : ''}`}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setLanguageMenuOpen(prev => !prev);
                    }}
                    aria-label="Change language"
                  >
                    <img
                      src={GlobeIcon}
                      alt="Language"
                      className="header-icon-language"
                    />
                  </button>
                  {languageMenuOpen &&
                    (() => {
                      const getLanguageDropdownPosition = () => {
                        if (!languageMenuRef.current)
                          return { top: 0, right: 0 };
                        const rect =
                          languageMenuRef.current.getBoundingClientRect();
                        const viewportWidth = window.innerWidth;

                        if (viewportWidth <= 768) {
                          return {
                            top: rect.bottom + window.scrollY + 8,
                            right: viewportWidth - rect.right,
                          };
                        }

                        if (viewportWidth <= 1024) {
                          return {
                            top: rect.bottom + window.scrollY + 4,
                            right: viewportWidth - rect.right,
                          };
                        }

                        return {
                          top: rect.bottom + window.scrollY,
                          right: viewportWidth - rect.right,
                        };
                      };

                      const position = toPagePx(getLanguageDropdownPosition());

                      return createPortal(
                        <div
                          className="admin-language-menu language-menu-positioned"
                          ref={languagePortalRef}
                          style={{
                            top: `${position.top}px`,
                            right: `${position.right}px`,
                          }}
                        >
                          {[
                            { code: 'hi', label: 'Hindi' },
                            { code: 'en', label: 'English' },
                          ].map(lang => (
                            <button
                              key={lang.code}
                              type="button"
                              className={`admin-language-option ${currentLanguage === lang.code ? 'active' : ''}`}
                              onClick={e => handleLanguageSelect(lang.code, e)}
                            >
                              <span
                                className={`admin-language-radio ${currentLanguage === lang.code ? 'active' : ''}`}
                              >
                                <span className="admin-language-radio-dot" />
                              </span>
                              <span className="admin-language-label">
                                {lang.label}
                              </span>
                            </button>
                          ))}
                        </div>,
                        document.body
                      );
                    })()}
                </div> */}

                <div className="admin-company-logo-container">
                  {companyData?.company_logo ? (
                    <img
                      src={
                        companyData.company_logo.startsWith('http')
                          ? companyData.company_logo
                          : `${import.meta.env.VITE_API_BASE_URL || ''}${companyData.company_logo}`
                      }
                      alt={companyData?.company_name || 'Company Logo'}
                    />
                  ) : actualCompanyLogo && !logoError ? (
                    <img
                      src={actualCompanyLogo}
                      alt={actualCompanyName || 'Company Logo'}
                      onError={() => setLogoError(true)}
                      onLoad={() => setLogoError(false)}
                      className="admin-company-logo-img"
                    />
                  ) : (
                    <div className="admin-company-logo-placeholder">
                      {companyData?.company_name?.[0]?.toUpperCase() ||
                        actualCompanyName?.[0]?.toUpperCase() ||
                        user?.name?.[0]?.toUpperCase() ||
                        'C'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* 
        {mobileSearchOpen && (
          <div className="admin-mobile-search-dropdown">
            <SimpleSearchBar
              placeholder="Search by brand, category, or city..."
              onSearch={handleSearch}
              showSearchButton={true}
              buttonLabel="Search"
              showDropdown={true}
              className="dashboard-search-bar"
            />
          </div>
        )}
        */}

        {/* {showBusinessFinancialToggle && (
          <div data-tour="mobile-dashboard-toggle" className="mobile-dashboard-toggle-bar">
            <Button
              variant={activeDashboard === 'financial' ? 'brand' : 'outline-secondary'}
              size="sm"
              onClick={() => {
                setActiveDashboard('financial');
                navigate(userRole?.role_name === 'sales_user' ? (isManufacturer ? '/mfg-dashboard' : '/tally-dashboard-v2') : '/financial-dashboard');
              }}
              className="mobile-dashboard-toggle-btn"
            >
              Financial Dashboard
            </Button>
            { 
            <Button
              variant={activeDashboard === 'business' ? 'brand' : 'outline-secondary'}
              size="sm"
              onClick={() => {
                setActiveDashboard('business');
                navigate('/dashboard');
              }}
              className="mobile-dashboard-toggle-btn"
            >
              Business Dashboard
            </Button>
            }
          </div>
        )} */}

        <main
          className={`admin-page-content ${isMessagesRoute ? 'admin-page-content--messages messages-route-height' : ''}`}
        >
          {children || <Outlet />}
        </main>
      </div>

      {sidebarOpen && (
        <div
          className="sidebar-backdrop show"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <ChatbotWidget />
    </div>
  );
}





